import axios from "axios";
import useStore from "../useStore";

const API_URL = process.env.REACT_APP_URL_API_PROD; //

const pendingRequests = {};
const MAX_CONCURRENT_REQUESTS = 10;
let userProfilePromise = null;
const globalFavorites = { data: null, loading: false };
const globalCategories = { data: null, loading: false };
const globalVinos = { data: null, loading: false };

const _cache = {
  data: {},
  timestamps: {},
  expiry: 15 * 60 * 1000,
  pendingRequests: {},

  get(key) {
    if (!this.data[key] || !this.timestamps[key]) return null;

    const now = Date.now();
    if (now - this.timestamps[key] > this.expiry) return null;

    return this.data[key];
  },

  set(key, value) {
    this.data[key] = value;
    this.timestamps[key] = Date.now();
  },

  clear(key = null) {
    if (key) {
      this.data[key] = null;
      this.timestamps[key] = null;
    } else {
      this.data = {};
      this.timestamps = {};
    }
  },

  async getOrFetch(key, fetchFn) {
    const cached = this.get(key);
    if (cached) return cached;

    if (this.pendingRequests[key]) {
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!this.pendingRequests[key]) {
            clearInterval(checkInterval);
            resolve(this.get(key) || null);
          }
        }, 50);
      });
    }

    this.pendingRequests[key] = true;

    try {
      const result = await fetchFn();
      this.set(key, result);
      return result;
    } finally {
      this.pendingRequests[key] = false;
    }
  },
};

async function limitConcurrency(key, requestFn) {
  while (Object.keys(pendingRequests).length >= MAX_CONCURRENT_REQUESTS) {
    await new Promise((resolve) => setTimeout(resolve, 50));
  }

  pendingRequests[key] = true;

  try {
    return await requestFn();
  } finally {
    delete pendingRequests[key];
  }
}

export async function listarCategoriasConProductosIniciales() {
  return _cache.getOrFetch("categoriasConProductos", async () => {
    try {
      const categoriasResp = await axios.get(`${API_URL}/products/categorias`);
      return { categorias: categoriasResp.data, productosIniciales: {} };
    } catch (error) {
      console.error("Error al listar categorías con productos:", error);
    }
  });
}

export async function listarCategorias() {
  if (globalCategories.loading) {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (!globalCategories.loading) {
          clearInterval(checkInterval);
          resolve(globalCategories.data || []);
        }
      }, 100);
    });
  }

  if (globalCategories.data) return globalCategories.data;

  globalCategories.loading = true;

  try {
    const cacheKey = "categorias";
    const cached = _cache.get(cacheKey);
    if (cached) {
      globalCategories.data = cached;
      return cached;
    }

    const resp = await axios.get(`${API_URL}/products/categorias`);
    _cache.set(cacheKey, resp.data);
    globalCategories.data = resp.data;
    return resp.data;
  } catch (error) {
    console.error("Error al listar categorías:", error);
    return [];
  } finally {
    globalCategories.loading = false;
  }
}

export async function listarTodosProductos() {
  return _cache.getOrFetch("todosProductos", async () => {
    try {
      const resp = await axios.get(`${API_URL}/products`);
      return resp.data;
    } catch (error) {
      console.error("Error al listar todos los productos:", error);
      return [];
    }
  });
}

export async function listarTiposCategoria(id) {
  return _cache.getOrFetch(`tiposCategoria_${id}`, async () => {
    try {
      const resp = await axios.get(
        `${API_URL}/products/categorias/${id}/tipos-categorias`
      );
      return resp.data;
    } catch (error) {
      console.error("Error al listar tipos de categorias", error);
      return [];
    }
  });
}

export async function listarTiposCategoriaMultiples(categoriaIds) {
  return _cache.getOrFetch(
    `tiposCategoriaMultiples_${categoriaIds.join("_")}`,
    async () => {
      try {
        const resp = await axios.get(
          `${API_URL}/products/tipos-categorias/multiple`,
          {
            params: { categorias: categoriaIds.join(",") },
          }
        );
        return resp.data;
      } catch (error) {
        const results = {};
        for (const id of categoriaIds) {
          try {
            const tipos = await listarTiposCategoria(id);
            results[id] = tipos;
          } catch (innerError) {
            results[id] = [];
          }
        }
        return results;
      }
    }
  );
}

export async function listarProductosByTipoCategoria(id) {
  return _cache.getOrFetch(`productosByTipo_${id}`, async () => {
    return limitConcurrency(`products_${id}`, async () => {
      try {
        const resp = await axios.get(`${API_URL}/products/tipoCategoria/${id}`);
        return resp.data;
      } catch (error) {
        console.error("Error al listar productos por tipo de categoría", error);
        return [];
      }
    });
  });
}

export async function listarProductosPorMultiplesCategorias(categoriaIds) {
  return _cache.getOrFetch(
    `productosPorCategorias_${categoriaIds.join("_")}`,
    async () => {
      try {
        const resp = await axios.get(
          `${API_URL}/products/categorias/multiple`,
          {
            params: { ids: categoriaIds.join(",") },
          }
        );
        return resp.data;
      } catch (error) {
        const results = {};
        for (const id of categoriaIds) {
          try {
            const productos = await listarProductosByTipoCategoria(id);
            results[id] = productos;
          } catch (e) {
            results[id] = [];
          }
        }
        return results;
      }
    }
  );
}

export async function listarTiposCategoriaVinos() {
  return _cache.getOrFetch("tiposCategoriaVinos", async () => {
    try {
      const resp = await axios.get(`${API_URL}/vinos/categorias`);
      return resp.data;
    } catch (error) {
      console.error("Error al listar categorías principales de vinos:", error);
      throw error;
    }
  });
}

export async function listarTiposCategoriaByCategoria(categoriaId) {
  if (!categoriaId) return [];

  return _cache.getOrFetch(
    `tiposCategoriaByCategoria_${categoriaId}`,
    async () => {
      try {
        const url = `${API_URL}/vinos/tipos-categorias?categoria=${categoriaId}`;
        const resp = await axios.get(url);

        let resultado = [];

        if (Array.isArray(resp.data)) {
          resultado = resp.data.filter(
            (tipo) =>
              tipo.categoria === categoriaId ||
              tipo.categoria?._id === categoriaId
          );
        } else if (resp.data && typeof resp.data === "object") {
          if (resp.data.data && Array.isArray(resp.data.data)) {
            resultado = resp.data.data.filter(
              (tipo) =>
                tipo.categoria === categoriaId ||
                tipo.categoria?._id === categoriaId
            );
          } else if (resp.data.results && Array.isArray(resp.data.results)) {
            resultado = resp.data.results.filter(
              (tipo) =>
                tipo.categoria === categoriaId ||
                tipo.categoria?._id === categoriaId
            );
          }
        }

        return resultado;
      } catch (error) {
        console.error(
          "Error al listar tipos de categorías por categoría principal:",
          error
        );
        return [];
      }
    }
  );
}

export async function listarVinosByTipoCategoria(idtipoCategoria) {
  if (!idtipoCategoria) return [];

  return _cache.getOrFetch(
    `vinosByTipoCategoria_${idtipoCategoria}`,
    async () => {
      return limitConcurrency(`vinos_${idtipoCategoria}`, async () => {
        try {
          const url = `${API_URL}/vinos/tipoCategoria/${idtipoCategoria}`;
          const resp = await axios.get(url);

          let resultado = [];

          if (Array.isArray(resp.data)) {
            resultado = resp.data;
          } else if (resp.data && typeof resp.data === "object") {
            if (resp.data.data && Array.isArray(resp.data.data)) {
              resultado = resp.data.data;
            } else if (resp.data.results && Array.isArray(resp.data.results)) {
              resultado = resp.data.results;
            } else if (resp.data.vinos && Array.isArray(resp.data.vinos)) {
              resultado = resp.data.vinos;
            }
          }

          return resultado;
        } catch (error) {
          console.error("Error al listar vinos por tipo de categoría:", error);
          return [];
        }
      });
    }
  );
}

export async function listarVinosByCategoria(categoriaId) {
  if (!categoriaId) return [];

  return _cache.getOrFetch(`vinosByCategoria_${categoriaId}`, async () => {
    try {
      const resp = await axios.get(
        `${API_URL}/vinos/categorias/${categoriaId}`
      );

      let resultado = [];

      if (resp.data) {
        if (Array.isArray(resp.data)) {
          resultado = resp.data;
        } else if (typeof resp.data === "object") {
          if (resp.data.vinos && Array.isArray(resp.data.vinos)) {
            resultado = resp.data.vinos;
          } else if (resp.data.results && Array.isArray(resp.data.results)) {
            resultado = resp.data.results;
          } else if (resp.data.data && Array.isArray(resp.data.data)) {
            resultado = resp.data.data;
          }
        }
      }

      return resultado;
    } catch (error) {
      console.error("Error al listar vinos por categoría:", error);
      return [];
    }
  });
}

export async function listarCategoriasVinos() {
  if (globalVinos.loading) {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (!globalVinos.loading) {
          clearInterval(checkInterval);
          resolve(globalVinos.data || []);
        }
      }, 100);
    });
  }

  if (globalVinos.data) return globalVinos.data;

  globalVinos.loading = true;

  try {
    const cacheKey = "categoriasVinos";
    const cached = _cache.get(cacheKey);
    if (cached) {
      globalVinos.data = cached;
      return cached;
    }

    const resp = await axios.get(`${API_URL}/vinos/categorias`);
    _cache.set(cacheKey, resp.data);
    globalVinos.data = resp.data;
    return resp.data;
  } catch (error) {
    console.error("Error al listar categorías:", error);
    return [];
  } finally {
    globalVinos.loading = false;
  }
}

export async function listarVinos() {
  return _cache.getOrFetch("todosVinos", async () => {
    try {
      const resp = await axios.get(`${API_URL}/vinos`);

      if (resp.data && Array.isArray(resp.data) && resp.data.length > 0) {
        const vinosPublicados = resp.data.filter(
          (vino) => vino.publicado === undefined || vino.publicado === true
        );
        return vinosPublicados;
      } else {
        throw new Error("No se encontraron vinos");
      }
    } catch (error) {
      console.error("Error al listar vinos:", error);
      return [];
    }
  });
}

export async function crearProducto(datos) {
  const resp = await axios.post(`${API_URL}/product`, datos);
  _cache.clear();
  return resp.data;
}

export async function crearUsuario({ nombre, email, pass }) {
  try {
    const resp = await axios.post(`${API_URL}/users/auth/register`, {
      nombre,
      email,
      password: pass,
    });
    return resp.data;
  } catch (error) {
    console.error("Error al crear usuario:", error);
    throw error;
  }
}

export async function iniciarSesion({ email, pass }) {
  try {
    const resp = await axios.post(`${API_URL}/users/auth/sign_in`, {
      email: email,
      password: pass,
    });
    return resp.data;
  } catch (error) {
    console.error("Error de inicio de sesión:", error);
    throw error;
  }
}

export async function recuperarUsuario(id, setUsuarioAutenticado) {
  try {
    const cacheKey = `usuario_${id}`;
    const cached = _cache.get(cacheKey);
    if (cached) {
      setUsuarioAutenticado(cached);
      return cached;
    }

    const resp = await axios.get(`${API_URL}/users/${id}`);
    _cache.set(cacheKey, resp.data);
    setUsuarioAutenticado(resp.data);
    return resp.data;
  } catch (error) {
    console.error("Error al recuperar usuario:", error);
    return null;
  }
}

export async function obtenerPerfil() {
  try {
    const token = useStore.getState().userToken;

    if (!token) return null;

    const cacheKey = "perfilUsuario";
    const cached = _cache.get(cacheKey);
    if (cached) return cached;

    if (userProfilePromise) return userProfilePromise;

    userProfilePromise = axios
      .get(`${API_URL}/users/profile`, {
        headers: {
          Authorization: `JWT ${token}`,
        },
      })
      .then((resp) => {
        _cache.set(cacheKey, resp.data);
        userProfilePromise = null;
        return resp.data;
      })
      .catch((error) => {
        console.error("Error al recuperar usuario:", error);
        userProfilePromise = null;
        return null;
      });

    return userProfilePromise;
  } catch (error) {
    console.error("Error al recuperar usuario:", error);
    return null;
  }
}

export async function listarUsuarios() {
  const resp = await axios.get(`${API_URL}/users`);
  return resp.data;
}

export async function actualizarUsuario({ id, email, nombre, pass }) {
  const resp = await axios.put(`${API_URL}/users/${id}`, {
    email: email,
    nombre: nombre,
    password: pass,
  });

  _cache.clear();

  return resp.data;
}

export async function obtenerFavoritos(token, forceReload = false) {
  try {
    if (!token) return [];

    if (!forceReload && globalFavorites.data) {
      return globalFavorites.data;
    }

    if (globalFavorites.loading) {
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!globalFavorites.loading) {
            clearInterval(checkInterval);
            resolve(globalFavorites.data || []);
          }
        }, 100);
      });
    }

    globalFavorites.loading = true;

    const cacheKey = "favoritos";
    if (!forceReload) {
      const cached = _cache.get(cacheKey);
      if (cached) {
        globalFavorites.data = cached;
        globalFavorites.loading = false;
        return cached;
      }
    }

    let userId;
    const perfilCached = _cache.get("perfilUsuario");

    if (perfilCached && perfilCached.user && perfilCached.user._id) {
      userId = perfilCached.user._id;
    } else {
      const profileResponse = await axios.get(`${API_URL}/users/profile`, {
        headers: {
          Authorization: `JWT ${token}`,
        },
      });

      if (
        !profileResponse.data ||
        !profileResponse.data.user ||
        !profileResponse.data.user._id
      ) {
        globalFavorites.loading = false;
        return [];
      }

      userId = profileResponse.data.user._id;
      _cache.set("perfilUsuario", profileResponse.data);
    }

    const resp = await axios.get(`${API_URL}/users/${userId}/favorites`, {
      headers: {
        Authorization: `JWT ${token}`,
      },
    });

    const favoritosIds = [];

    if (Array.isArray(resp.data)) {
      resp.data.forEach((fav) => {
        if (typeof fav === "string") {
          favoritosIds.push(fav);
        } else if (fav && typeof fav === "object") {
          const id = fav._id || fav.id;
          if (id) favoritosIds.push(id);
        }
      });
    } else if (resp.data && typeof resp.data === "object") {
      if (resp.data.favoritos && Array.isArray(resp.data.favoritos)) {
        resp.data.favoritos.forEach((fav) => {
          if (typeof fav === "string") {
            favoritosIds.push(fav);
          } else if (fav && typeof fav === "object") {
            const id = fav._id || fav.id;
            if (id) favoritosIds.push(id);
          }
        });
      }
    }

    if (favoritosIds.length === 0) {
      _cache.set(cacheKey, []);
      globalFavorites.data = [];
      globalFavorites.loading = false;
      return [];
    }

    let todosProductos = [];
    let todosVinos = [];

    const productosCached = _cache.get("todosProductos");
    if (productosCached) {
      todosProductos = productosCached;
    } else {
      const productosRes = await axios.get(`${API_URL}/products`, {
        headers: { Authorization: `JWT ${token}` },
      });
      todosProductos = Array.isArray(productosRes.data)
        ? productosRes.data
        : [];
      _cache.set("todosProductos", todosProductos);
    }

    const vinosCached = _cache.get("todosVinos");
    if (vinosCached) {
      todosVinos = vinosCached;
    } else {
      const vinosRes = await axios.get(`${API_URL}/vinos`, {
        headers: { Authorization: `JWT ${token}` },
      });
      todosVinos = Array.isArray(vinosRes.data) ? vinosRes.data : [];
      _cache.set("todosVinos", todosVinos);
    }

    const favoritosConDetalles = [];

    favoritosIds.forEach((favId) => {
      const productoEncontrado = todosProductos.find(
        (p) => p._id === favId || p.id === favId || p._id?.$oid === favId
      );

      if (productoEncontrado) {
        favoritosConDetalles.push({
          ...productoEncontrado,
          esProducto: true,
        });
        return;
      }

      const vinoEncontrado = todosVinos.find(
        (v) => v._id === favId || v.id === favId || v._id?.$oid === favId
      );

      if (vinoEncontrado) {
        favoritosConDetalles.push({
          ...vinoEncontrado,
          esVino: true,
        });
        return;
      }

      favoritosConDetalles.push({
        _id: favId,
        nombre: `Favorito ${favId.substring(0, 6)}...`,
        precio: "",
        descripcion: "",
      });
    });

    _cache.set(cacheKey, favoritosConDetalles);
    globalFavorites.data = favoritosConDetalles;
    return favoritosConDetalles;
  } catch (error) {
    console.error("Error al obtener favoritos:", error);
    return [];
  } finally {
    globalFavorites.loading = false;
  }
}

export async function agregarFavorito(token, productoId, esVino = false) {
  try {
    if (!token || !productoId) {
      throw new Error("Token o productoId no definido");
    }

    let userId;
    const perfilCached = _cache.get("perfilUsuario");

    if (perfilCached && perfilCached.user && perfilCached.user._id) {
      userId = perfilCached.user._id;
    } else {
      const profileResponse = await axios.get(`${API_URL}/users/profile`, {
        headers: {
          Authorization: `JWT ${token}`,
        },
      });

      if (
        !profileResponse.data ||
        !profileResponse.data.user ||
        !profileResponse.data.user._id
      ) {
        throw new Error("No se pudo obtener el ID de usuario desde el perfil");
      }

      userId = profileResponse.data.user._id;
      _cache.set("perfilUsuario", profileResponse.data);
    }

    const response = await axios.post(
      `${API_URL}/users/${userId}/favorites/${productoId}`,
      { tipoFavorito: esVino ? "Vinos" : "Productos" },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `JWT ${token}`,
        },
      }
    );

    _cache.clear("favoritos");
    globalFavorites.data = null;

    return response.data;
  } catch (error) {
    console.error(
      `Error al añadir favorito ${esVino ? "(vino)" : "(producto)"}:`,
      error
    );
    throw error;
  }
}

export async function eliminarFavorito(token, productoId) {
  try {
    if (!token || !productoId) {
      throw new Error("Token o productoId no definido");
    }

    let userId;
    const perfilCached = _cache.get("perfilUsuario");

    if (perfilCached && perfilCached.user && perfilCached.user._id) {
      userId = perfilCached.user._id;
    } else {
      const profileResponse = await axios.get(`${API_URL}/users/profile`, {
        headers: {
          Authorization: `JWT ${token}`,
        },
      });

      if (
        !profileResponse.data ||
        !profileResponse.data.user ||
        !profileResponse.data.user._id
      ) {
        throw new Error("No se pudo obtener el ID de usuario desde el perfil");
      }

      userId = profileResponse.data.user._id;
      _cache.set("perfilUsuario", profileResponse.data);
    }

    const response = await axios.delete(
      `${API_URL}/users/${userId}/favorites/${productoId}`,
      {
        headers: {
          Authorization: `JWT ${token}`,
        },
      }
    );

    _cache.clear("favoritos");
    globalFavorites.data = null;

    return response.data;
  } catch (error) {
    console.error("Error al eliminar favorito:", error);
    throw error;
  }
}

export async function crearHistorialPedido(token, productos) {
  try {
    let userId;
    const perfilCached = _cache.get("perfilUsuario");

    if (perfilCached && perfilCached.user && perfilCached.user._id) {
      userId = perfilCached.user._id;
    } else {
      const profileResponse = await axios.get(`${API_URL}/users/profile`, {
        headers: {
          Authorization: `JWT ${token}`,
        },
      });

      if (
        !profileResponse.data ||
        !profileResponse.data.user ||
        !profileResponse.data.user._id
      ) {
        throw new Error("No se pudo obtener el ID de usuario desde el perfil");
      }

      userId = profileResponse.data.user._id;
      _cache.set("perfilUsuario", profileResponse.data);
    }

    const productosFormateados = productos.map((p) => {
      return {
        _id: p._id || p.id,
        nombre:
          p.nombre || (p.esVino ? "Vino sin nombre" : "Producto sin nombre"),
        precio: p.precio || 0,
        cantidad: p.cantidad || 1,
        esVino: p.esVino === true,
      };
    });

    try {
      const response = await axios.post(
        `${API_URL}/HistorialPedidos`,
        {
          User: userId,
          Productos: productosFormateados,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `JWT ${token}`,
          },
        }
      );

      _cache.clear("historialPedidos");

      return response.data;
    } catch (axiosError) {
      const fetchResponse = await fetch(`${API_URL}/HistorialPedidos`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `JWT ${token}`,
        },
        body: JSON.stringify({
          User: userId,
          Productos: productosFormateados,
        }),
      });

      if (!fetchResponse.ok) {
        const errorText = await fetchResponse.text();
        throw new Error(
          "Error al guardar el pedido en el historial: " + errorText
        );
      }

      const data = await fetchResponse.json();
      return data;
    }
  } catch (error) {
    console.error("Error en crearHistorialPedido:", error);
    throw error;
  }
}

export async function obtenerHistorialPedidos(userId) {
  return _cache.getOrFetch(`historialPedidos_${userId}`, async () => {
    try {
      const response = await fetch(
        `${API_URL}/HistorialPedidos/usuario/${userId}`
      );

      if (!response.ok) {
        throw new Error("Error al obtener el historial de pedidos");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error en obtenerHistorialPedidos:", error);
      throw error;
    }
  });
}

export async function obtenerHistorialPedido(pedidoId) {
  return _cache.getOrFetch(`historialPedido_${pedidoId}`, async () => {
    try {
      const response = await fetch(`${API_URL}/HistorialPedidos/${pedidoId}`);

      if (!response.ok) {
        throw new Error("Error al obtener el pedido");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error en obtenerHistorialPedido:", error);
      throw error;
    }
  });
}

export async function compartirPedido(datosCompartir) {
  try {
    const { usuarioId: token, ...datosBase } = datosCompartir;

    if (!token || !datosBase.pedidoId) {
      throw new Error("Faltan datos obligatorios para compartir el pedido");
    }

    let userId, nombreUsuario;
    const perfilCached = _cache.get("perfilUsuario");

    if (perfilCached && perfilCached.user) {
      userId = perfilCached.user._id;
      nombreUsuario =
        perfilCached.user.nombre || datosBase.nombreUsuario || "Usuario";
    } else {
      const profileResponse = await axios.get(`${API_URL}/users/profile`, {
        headers: {
          Authorization: `JWT ${token}`,
        },
      });

      if (
        !profileResponse.data ||
        !profileResponse.data.user ||
        !profileResponse.data.user._id
      ) {
        throw new Error("No se pudo obtener el ID de usuario desde el perfil");
      }

      userId = profileResponse.data.user._id;
      nombreUsuario =
        profileResponse.data.user.nombre ||
        datosBase.nombreUsuario ||
        "Usuario";
      _cache.set("perfilUsuario", profileResponse.data);
    }

    const datosConUsuarioReal = {
      ...datosBase,
      usuarioId: userId,
      nombreUsuario: nombreUsuario,
    };

    try {
      const response = await axios.post(
        `${API_URL}/recomendaciones`,
        datosConUsuarioReal,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `JWT ${token}`,
          },
        }
      );

      _cache.clear("recomendaciones");

      return response.data;
    } catch (recomendacionesError) {
      try {
        const historialResponse = await axios.post(
          `${API_URL}/HistorialPedidos`,
          {
            User: userId,
            Productos: datosConUsuarioReal.productos,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `JWT ${token}`,
            },
          }
        );

        return historialResponse.data;
      } catch (historialError) {
        throw historialError;
      }
    }
  } catch (error) {
    console.error("Error en compartirPedido:", error);
    throw error;
  }
}

export async function obtenerRecomendaciones() {
  return _cache.getOrFetch("recomendaciones", async () => {
    try {
      const response = await fetch(`${API_URL}/recomendaciones`);

      const responseText = await response.text();
      let data;

      try {
        data = JSON.parse(responseText);
      } catch (jsonError) {
        throw new Error(`Respuesta no válida: ${responseText}`);
      }

      if (!response.ok) {
        throw new Error(
          `Error al obtener las recomendaciones: ${response.status}`
        );
      }

      return data;
    } catch (error) {
      console.error("Error en obtenerRecomendaciones:", error);
      throw error;
    }
  });
}

export async function obtenerRecomendacionPorId(id) {
  return _cache.getOrFetch(`recomendacion_${id}`, async () => {
    try {
      if (!id) {
        throw new Error("ID de recomendación no proporcionado");
      }

      const response = await fetch(`${API_URL}/recomendaciones/${id}`);

      const responseText = await response.text();
      let data;

      try {
        data = JSON.parse(responseText);
      } catch (jsonError) {
        throw new Error(`Respuesta no válida: ${responseText}`);
      }

      if (!response.ok) {
        throw new Error(
          `Error al obtener la recomendación: ${
            response.status
          } - ${JSON.stringify(data)}`
        );
      }

      if (!data || typeof data !== "object") {
        throw new Error(
          "La respuesta no tiene el formato esperado de una recomendación"
        );
      }

      return data;
    } catch (error) {
      console.error("Error en obtenerRecomendacionPorId:", error);
      throw error;
    }
  });
}

export async function importarRecomendacion(recomendacionId, usuarioId) {
  try {
    if (!recomendacionId || !usuarioId) {
      throw new Error("Faltan datos necesarios para importar la recomendación");
    }

    const response = await fetch(
      `${API_URL}/recomendaciones/${recomendacionId}/importar`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ usuarioId }),
      }
    );

    if (!response.ok) {
      throw new Error("Error al importar la recomendación");
    }

    const data = await response.json();

    _cache.clear("recomendaciones");
    _cache.clear(`misRecomendaciones_${usuarioId}`);

    return data;
  } catch (error) {
    console.error("Error en importarRecomendacion:", error);
    throw error;
  }
}

export async function eliminarRecomendacion(recomendacionId, usuarioId) {
  try {
    if (!recomendacionId) {
      throw new Error("Falta el ID de la recomendación para eliminarla");
    }

    const response = await fetch(
      `${API_URL}/recomendaciones/${recomendacionId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ usuarioId }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error("Error al eliminar la recomendación");
    }

    const data = await response.json();

    _cache.clear("recomendaciones");
    _cache.clear(`misRecomendaciones_${usuarioId}`);

    return data;
  } catch (error) {
    console.error("Error en eliminarRecomendacion:", error);
    throw error;
  }
}

export async function obtenerMisRecomendaciones(usuarioId) {
  return _cache.getOrFetch(`misRecomendaciones_${usuarioId}`, async () => {
    try {
      if (!usuarioId) {
        throw new Error("ID de usuario requerido");
      }

      const response = await fetch(
        `${API_URL}/recomendaciones/usuario/${usuarioId}`
      );

      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (jsonError) {
        throw new Error(`Respuesta no válida: ${responseText}`);
      }

      if (!response.ok) {
        throw new Error(
          `Error al obtener mis recomendaciones: ${response.status}`
        );
      }

      return data;
    } catch (error) {
      console.error("Error en obtenerMisRecomendaciones:", error);
      try {
        const alternativeResponse = await fetch(
          `${API_URL}/recomendaciones/importaciones/${usuarioId}`
        );

        if (alternativeResponse.ok) {
          return await alternativeResponse.json();
        }
      } catch (altError) {
        console.error("Error al intentar método alternativo:", altError);
      }

      return [];
    }
  });
}

export const obtenerAlergenos = async () => {
  return _cache.getOrFetch("alergenos", async () => {
    try {
      const respuesta = await fetch(`${API_URL}/products/alergenos`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!respuesta.ok) {
        throw new Error(`Error al obtener alérgenos: ${respuesta.statusText}`);
      }

      return await respuesta.json();
    } catch (error) {
      console.error("Error en la API obtenerAlergenos:", error);
      throw error;
    }
  });
};

export async function obtenerOrdenCategorias() {
  return _cache.getOrFetch("ordenCategorias", async () => {
    try {
      const resp = await axios.get(`${API_URL}/products/categorias`);
      return resp.data;
    } catch (error) {
      console.error("Error al obtener categorías ordenadas:", error);
      return [];
    }
  });
}

export async function obtenerOrdenSubcategorias(categoriaId) {
  return _cache.getOrFetch(`ordenSubcategorias_${categoriaId}`, async () => {
    try {
      const resp = await axios.get(
        `${API_URL}/products/categorias/${categoriaId}/tipos-categorias`
      );
      return resp.data;
    } catch (error) {
      console.error("Error al listar tipos de categorias ordenados", error);
      return [];
    }
  });
}

export async function actualizarOrdenCategorias(ordenCategorias) {
  try {
    const resp = await axios.put(
      `${API_URL}/orden/categorias`,
      ordenCategorias,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    _cache.clear("ordenCategorias");
    _cache.clear("categorias");
    globalCategories.data = null;

    return resp.data;
  } catch (error) {
    console.error("Error al actualizar orden de categorías:", error);
    throw error;
  }
}

export async function actualizarOrdenSubcategorias(ordenSubcategorias) {
  try {
    const resp = await axios.put(
      `${API_URL}/orden/subcategorias`,
      ordenSubcategorias,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    Object.keys(_cache.data).forEach((key) => {
      if (
        key.startsWith("ordenSubcategorias_") ||
        key.startsWith("tiposCategorias")
      ) {
        _cache.data[key] = null;
        _cache.timestamps[key] = null;
      }
    });

    return resp.data;
  } catch (error) {
    console.error("Error al actualizar orden de subcategorías:", error);
    throw error;
  }
}

export async function actualizarOrdenProductos(ordenProductos) {
  try {
    const resp = await axios.put(`${API_URL}/orden/productos`, ordenProductos, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    Object.keys(_cache.data).forEach((key) => {
      if (key.startsWith("productos") || key.includes("Productos")) {
        _cache.data[key] = null;
        _cache.timestamps[key] = null;
      }
    });

    return resp.data;
  } catch (error) {
    console.error("Error al actualizar orden de productos:", error);
    throw error;
  }
}

export async function actualizarOrdenVinos(ordenVinos) {
  try {
    const resp = await axios.put(`${API_URL}/orden/vinos`, ordenVinos, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    Object.keys(_cache.data).forEach((key) => {
      if (key.includes("vinos") || key.includes("Vinos")) {
        _cache.data[key] = null;
        _cache.timestamps[key] = null;
      }
    });

    return resp.data;
  } catch (error) {
    console.error("Error al actualizar orden de vinos:", error);
    throw error;
  }
}

export async function sendMessage(jsonPedido) {
  const resp = await axios.post(
    "http://localhost:3000/messages/send-email",
    jsonPedido
  );
  console.log("pedido enviado", resp);
  return resp.data;
}
